import { redirect } from 'react-router';
import { lazyRoute } from '@/navigation/utils/lazyRoute';
import { SERVICE_AREA_PATH } from '@gearghost/page-meta';
import { PATHS } from '@/navigation/paths';
import { salesTaxGuideLoader } from '@/navigation/loaders/salesTaxLoaders';
import { blogPostLoader } from '@/navigation/loaders/blogLoaders';

/**
 * Public routes - accessible without authentication.
 * Uses RootLayout (Header + Footer).
 *
 * Every page here is open to signed-out visitors: the closed-beta gate that
 * once bounced them off the browse pages is gone (Tyler, 2026-09-27: "we are
 * never putting that gate back up").
 */
export const publicRoutes = [
  // Home
  {
    index: true,
    lazy: lazyRoute(() => import('@/component/pages/home')),
  },
  // Browse & Search
  {
    path: 'category',
    lazy: lazyRoute(() => import('@/component/pages/category')),
  },
  {
    path: 'search-listings',
    lazy: lazyRoute(() => import('@/component/pages/search-listings')),
  },
  {
    path: 'vendor/:vendorId',
    lazy: lazyRoute(() => import('@/component/pages/vendors/vendor-detail')),
  },
  {
    path: 'listing/:listingId',
    lazy: lazyRoute(() => import('@/component/pages/listing-detail')),
  },
  {
    path: 'store-review',
    lazy: lazyRoute(() => import('@/component/pages/vendors/store-review')),
  },
  {
    path: 'renter/:renterId',
    lazy: lazyRoute(() => import('@/component/pages/renters/renter-detail')),
  },
  // QR-code landing page (the URL is printed on physical marketing
  // material): explains GearGhost and walks through signup. NO loader on
  // purpose: guestLoader ejected any browser holding a valid token toward
  // the dashboard, and a half-dead session then cascaded away from the page.
  // This page renders for every visitor, unconditionally;
  // publicRoutes.test.jsx pins it.
  {
    path: 'lastlooks',
    lazy: lazyRoute(() => import('@/component/pages/lastlooks')),
  },
  // Info pages
  {
    path: 'aboutus',
    lazy: lazyRoute(() => import('@/component/pages/about-us')),
  },
  {
    path: 'how-it-works',
    lazy: lazyRoute(() => import('@/component/pages/how-it-works')),
  },
  {
    path: 'contactus',
    lazy: lazyRoute(() => import('@/component/pages/contact-us')),
  },
  {
    // Play Data safety delete-account URL — path printed in the Play
    // Console form, never rename (see PATHS.deleteAccount)
    path: 'delete-account',
    lazy: lazyRoute(() => import('@/component/pages/delete-account')),
  },
  {
    path: 'damage-and-theft',
    lazy: lazyRoute(() => import('@/component/pages/damage-and-theft')),
  },
  // Email unsubscribe landing (GG-493). NO loader on purpose: the URL is
  // baked into every sent registry email, and a signed-out recipient must
  // be able to unsubscribe (a compliance problem, not just a UX one).
  {
    path: 'unsubscribe',
    lazy: lazyRoute(() => import('@/component/pages/unsubscribe')),
  },
  // Legal pages
  {
    path: 'terms-of-service',
    lazy: lazyRoute(() => import('@/component/pages/terms-of-service')),
  },
  {
    path: 'open-source-licenses',
    lazy: lazyRoute(() => import('@/component/pages/open-source-licenses')),
  },
  {
    path: 'privacy-policy',
    lazy: lazyRoute(() => import('@/component/pages/privacy-policy')),
  },
  {
    path: 'rules-and-guidelines',
    lazy: lazyRoute(() => import('@/component/pages/rules-and-guidelines')),
  },
  // Guides: the list page and each guide (Tyler, 2026-09-23).
  {
    path: 'guides',
    lazy: lazyRoute(() => import('@/component/pages/guides')),
  },
  {
    path: 'guides/savings-tiers',
    lazy: lazyRoute(() => import('@/component/pages/guides/savings-tiers')),
  },
  {
    path: 'guides/sales-tax',
    // Not found while an admin has the Sales Tax switch off (2026-10-21).
    loader: salesTaxGuideLoader,
    lazy: lazyRoute(() => import('@/component/pages/guides/sales-tax')),
  },
  {
    path: 'guides/listings',
    lazy: lazyRoute(() => import('@/component/pages/guides/listings')),
  },
  {
    // PATHS.vendorApiGuide without its leading slash: the address is the schemas' own (vendorApiDocs).
    path: PATHS.vendorApiGuide.slice(1),
    lazy: lazyRoute(() => import('@/component/pages/guides/vendor-api')),
  },
  // The blog's list of posts, and a post's own page (docs/plans/blog.md, The pages 1 and 2).
  {
    // PATHS.blog without its leading slash: the address is the blog package's own.
    path: PATHS.blog.slice(1),
    lazy: lazyRoute(() => import('@/component/pages/blog')),
  },
  {
    path: `${PATHS.blog.slice(1)}/:slug`,
    // Not found for an address with no post at it.
    loader: blogPostLoader,
    lazy: lazyRoute(() => import('@/component/pages/blog/post')),
  },
  // A category's page (Tyler, 2026-10-27): the search held to one category,
  // at page-meta's categoryPath, /los-angeles/<category>. The page answers
  // not found for an address that names no category.
  {
    path: `${SERVICE_AREA_PATH.slice(1)}/:category`,
    lazy: lazyRoute(() => import('@/component/pages/category-rentals')),
  },
  // The Total Listing Stock guide grew into the listings guide (Tyler,
  // 2026-09-29); its address lands on that guide's stock section.
  {
    path: 'guides/total-stock',
    loader: () => redirect(PATHS.totalStock),
  },
  // The guides' addresses before the move. Installed app builds open
  // /how-it-works/savings-tiers from the Account screen, and bookmarks
  // point at all three, so these redirect for good. A loader redirects
  // before RootLayout renders, so its ID-verification bounce never sees
  // the old path.
  {
    path: 'how-it-works/savings-tiers',
    loader: () => redirect(PATHS.savingsTiers),
  },
  {
    path: 'how-it-works/sales-tax',
    loader: () => redirect(PATHS.salesTax),
  },
  {
    path: 'how-it-works/total-stock',
    loader: () => redirect(PATHS.totalStock),
  },
  // /home was never a page, but Google holds the address and sent a click
  // to it (Search Console, 2026-10-27), so it lands on the home page rather
  // than answering not found.
  {
    path: 'home',
    loader: () => redirect(PATHS.home),
  },
];
